import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import { useAnalyticsContext } from '../analytics-context';
import { formatCurrency } from '../../../../../lib/utils';

const SummaryValue: React.FC<{
  isLoading: boolean;
  children: React.ReactNode;
}> = ({ isLoading, children }) => {
  if (isLoading) {
    return (
      <div className="h-6 w-24 animate-pulse rounded bg-muted/60" />
    );
  }
  return (
    <span className="text-2xl font-semibold text-foreground">
      {children}
    </span>
  );
};

export const AnalyticsSummary: React.FC = () => {
  const { users, orders, downloads, transactions, selectedFilter, payload } =
    useAnalyticsContext();

  const awaitingCustomRange =
    selectedFilter === 'custom' && payload === null;

  const cards = [
    {
      title: 'New Users',
      value: users.data?.totalCount ?? 0,
      loading: users.isLoading,
      subtitle: 'Signups within the selected period.',
      formatter: (val: number) => val.toLocaleString(),
    },
    {
      title: 'Total Orders',
      value: orders.data?.totalOrders ?? 0,
      loading: orders.isLoading,
      subtitle: `Completion ${orders.data?.completionRate ?? 0}% • Paid ${
        orders.data?.paymentRate ?? 0
      }%`,
      formatter: (val: number) => val.toLocaleString(),
    },
    {
      title: 'File Downloads',
      value: downloads.data?.totalDownloads ?? 0,
      loading: downloads.isLoading,
      subtitle: 'Total download count across digital files.',
      formatter: (val: number) => val.toLocaleString(),
    },
    {
      title: 'Payments Volume',
      value: transactions.data?.summary?.totalAmount ?? 0,
      loading: transactions.isLoading,
      subtitle: `${
        transactions.data?.summary?.totalTransactions ?? 0
      } successful transactions.`,
      formatter: (val: number) => formatCurrency(val),
    },
  ];

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {cards.map(card => (
        <Card key={card.title} className="border-none shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {card.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {awaitingCustomRange ? (
              <div className="text-muted-foreground text-sm">
                Select a custom date range to view this metric.
              </div>
            ) : (
              <>
                <SummaryValue isLoading={card.loading}>
                  {card.formatter(card.value)}
                </SummaryValue>
                <p className="text-xs text-muted-foreground mt-2">
                  {card.subtitle}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

